import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { WingrLockup } from '../components/WingrMascot';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    telegram: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username || formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Name is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const telegramClean = formData.telegram.replace(/^@/, '');
    const telegramRegex = /^[a-zA-Z0-9_]{5,32}$/;
    if (!formData.telegram || !telegramRegex.test(telegramClean)) {
      newErrors.telegram = 'Telegram username must be 5-32 characters (alphanumeric and underscores only)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/register', {
        username: formData.username,
        password: formData.password,
        name: formData.name,
        email: formData.email,
        telegram: formData.telegram
      });

      if (response.data.success) {
        toast.success('Registration successful! Your account is pending approval.');
        // Store token for immediate login
        localStorage.setItem('token', response.data.token);
        navigate('/login', { 
          state: { 
            message: 'Registration successful! Your account is pending approval. You will be notified once approved.' 
          } 
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Registration failed';
      toast.error(errorMessage);
      if (error.response?.data?.error) {
        // Set field-specific errors if available
        if (error.response.data.error.includes('username')) {
          setErrors({ username: error.response.data.error });
        } else if (error.response.data.error.includes('email')) {
          setErrors({ email: error.response.data.error });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center py-4 sm:py-6 px-4 sm:px-6 lg:px-8" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}>
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl ${
          isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/30'
        }`} />
        <div className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl ${
          isDarkMode ? 'bg-cyan-600/15' : 'bg-cyan-300/25'
        }`} />
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl ${
          isDarkMode ? 'bg-sky-600/10' : 'bg-sky-300/20'
        }`} />
      </div>

      {/* Dark mode toggle button */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-white/20 dark:hover:bg-gray-700/30 transition-colors duration-200 z-50"
        title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDarkMode ? (
          <SunIcon className="h-6 w-6" />
        ) : (
          <MoonIcon className="h-6 w-6" />
        )}
      </button>
      
      <div className="max-w-md w-full relative z-10" style={{ position: 'relative', zIndex: 10000 }}>
        {/* Register Card */}
        <div className="glass-card rounded-3xl p-5 sm:p-6">
          {/* Logo - Top Left */}
          <div className="flex items-start justify-between mb-4">
            <Link to="/" className="cursor-pointer hover:opacity-80 transition-opacity">
              <WingrLockup size={36} />
            </Link>
            <div className="text-right">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Create Account
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                Register for WingR AI
              </p>
            </div>
          </div>
          <form 
            className="space-y-2.5" 
            onSubmit={handleSubmit}
          >
            <div className="space-y-2">
              <div>
                <label htmlFor="username" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  className={`glass-input w-full px-3 py-2 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    errors.username ? 'focus:ring-red-500 border-red-300 dark:border-red-500' : 'focus:ring-blue-500'
                  }`}
                  placeholder="Username (min 3)"
                  value={formData.username}
                  onChange={handleChange}
                  style={{ pointerEvents: 'auto' }}
                />
                {errors.username && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.username}</p>
                )}
              </div>

              <div>
                <label htmlFor="name" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className={`glass-input w-full px-3 py-2 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    errors.name ? 'focus:ring-red-500 border-red-300 dark:border-red-500' : 'focus:ring-blue-500'
                  }`}
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{ pointerEvents: 'auto' }}
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`glass-input w-full px-3 py-2 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    errors.email ? 'focus:ring-red-500 border-red-300 dark:border-red-500' : 'focus:ring-blue-500'
                  }`}
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  style={{ pointerEvents: 'auto' }}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="telegram" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telegram
                </label>
                <input
                  id="telegram"
                  name="telegram"
                  type="text"
                  required
                  className={`glass-input w-full px-3 py-2 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    errors.telegram ? 'focus:ring-red-500 border-red-300 dark:border-red-500' : 'focus:ring-blue-500'
                  }`}
                  placeholder="@username"
                  value={formData.telegram}
                  onChange={handleChange}
                  style={{ pointerEvents: 'auto' }}
                />
                {errors.telegram && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.telegram}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={`glass-input w-full px-3 py-2 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    errors.password ? 'focus:ring-red-500 border-red-300 dark:border-red-500' : 'focus:ring-blue-500'
                  }`}
                  placeholder="Password (min 6)"
                  value={formData.password}
                  onChange={handleChange}
                  style={{ pointerEvents: 'auto' }}
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={`glass-input w-full px-3 py-2 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    errors.confirmPassword ? 'focus:ring-red-500 border-red-300 dark:border-red-500' : 'focus:ring-blue-500'
                  }`}
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  style={{ pointerEvents: 'auto' }}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center items-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:scale-105' 
                    : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:scale-105'
                }`}
                style={{ pointerEvents: 'auto', cursor: loading ? 'wait' : 'pointer' }}
              >
                {loading ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold bg-gradient-to-r from-blue-500 to-sky-500 bg-clip-text text-transparent hover:from-blue-400 hover:to-sky-400">
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;

